/* AppraisalComponents/helpers/baytrayLinkCascade.js
 * Phase C · C4d cross-section link cascade extraction (2026-02c).
 *
 * Pure helpers for the Aquaseal ↔ Flashing back-tray link graph. These
 * functions have zero React dependency and zero implicit state — they
 * take a `prev` groups object + the deleted row and return the
 * *delta* to be merged back in.
 *
 * The extraction sets up the future `row_link` engine primitive
 * (BAY-* rules · post-go-live). The engine will one day emit
 * declarative row-mutation instructions (delete-cascade / decrement-
 * qty / clear-reciprocal); today these helpers encode that same
 * behaviour imperatively. When the engine primitive lands, only these
 * two functions change · the removeLine caller stays intact.
 *
 * Contract:
 *   • `_aquasealLineMatch(lines, payload)`
 *       Finds the Aquaseal line paired with a dektite payload.
 *       Idempotent · supplier_code wins over dektite_name.
 *
 *   • `cascadeDeleteFlashingToAquaseal(prev, deletedRow)`
 *       When a Flashing row is deleted:  decrement each paired
 *       Aquaseal line's qty by 1 for every entry in
 *       row.linked_dektites; drop rows whose qty hits zero.
 *       Returns { Aquaseal: newList } or {} if nothing changed.
 *
 *   • `cascadeDeleteAquasealToFlashing(prev, deletedRow)`
 *       When an Aquaseal row is deleted with a linked_backtray:
 *         · source === 'created' → drop the auto-minted Flashing row
 *         · source === 'picked'  → keep the Flashing row but clear
 *           its reciprocal linked_dektites entry so the chip vanishes
 *       Returns { Flashing: newList } or {} if nothing changed.
 */

export function _aquasealLineMatch(lines, payload) {
  // Matching rules used by both upsert AND cascade-delete. supplier_code
  // is authoritative when present — falls back to type_name match.
  if (payload.supplier_code) {
    const byCode = (lines || []).find(l =>
      l.component_type === 'Aquaseal'
      && (l.supplier_code || '').trim() === payload.supplier_code);
    if (byCode) return byCode;
  }
  if (payload.dektite_name) {
    const byName = (lines || []).find(l =>
      l.component_type === 'Aquaseal'
      && (l.type_name || '').trim() === payload.dektite_name.trim());
    if (byName) return byName;
  }
  return null;
}


export function cascadeDeleteFlashingToAquaseal(prev, deletedRow) {
  // Iter233 · Flashing deleted → decrement paired Aquaseal rows.
  if (!Array.isArray(deletedRow?.linked_dektites) || !deletedRow.linked_dektites.length) {
    return {};
  }
  let aqPool = [...(prev.Aquaseal || [])];
  let touched = false;
  for (const dektite of deletedRow.linked_dektites) {
    const matched = _aquasealLineMatch(aqPool, dektite);
    if (!matched) continue;
    touched = true;
    const remaining = (Number(matched.quantity) || 0) - 1;
    if (remaining <= 0) {
      aqPool = aqPool.filter(l => l._key !== matched._key);
    } else {
      aqPool = aqPool.map(l => l._key === matched._key
        ? { ...l, quantity: remaining }
        : l);
    }
  }
  return touched ? { Aquaseal: aqPool } : {};
}


export function cascadeDeleteAquasealToFlashing(prev, deletedRow) {
  // Iter234 · Aquaseal deleted → drop or clean paired Flashing rows.
  const lb = deletedRow?.linked_backtray;
  if (!lb) return {};
  const flashingKey = lb.line_key;
  if (lb.source === 'created') {
    // Auto-minted backtray Flashing row · drop it entirely.
    return {
      Flashing: (prev.Flashing || []).filter(l => l._key !== flashingKey),
    };
  }
  if (!flashingKey) return {};
  // source === 'picked' · clear the reciprocal linked_dektites entry.
  let touched = false;
  const nextFlashing = (prev.Flashing || []).map(l => {
    if (l._key !== flashingKey) return l;
    const list = Array.isArray(l.linked_dektites) ? [...l.linked_dektites] : [];
    const matchIdx = list.findIndex(d =>
      (d.supplier_code && d.supplier_code === (deletedRow.supplier_code || ''))
      || (d.dektite_name && d.dektite_name === (deletedRow.type_name || ''))
    );
    if (matchIdx >= 0) {
      list.splice(matchIdx, 1);
      touched = true;
    }
    return { ...l, linked_dektites: list };
  });
  return touched ? { Flashing: nextFlashing } : {};
}
